/**
 * Public policy context — surfaces the currency, tax and tip
 * configuration that the admin sets under "Policies & Settings".
 *
 * Two access patterns are supported:
 *
 *   1. React components -> `const { policy, fmt, ... } = usePolicy()`
 *   2. Non-React code (e.g. the receipt HTML builder, lib/realtime
 *      `fmtMoney`) -> `getCurrentCurrency()` / `getCurrentPolicy()`.
 *
 * Anyone can read the public policy — no auth required — because the
 * storefront menu and iPad kiosk need the currency before login.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { api } from '@/lib/api';

const DEFAULT_POLICY = {
  currency_code: 'USD',
  tax_rate: 0.08,
  tax_enabled: true,
  tax_show_on_receipt: true,
  tip_show_on_receipt: true,
  tip_suggestions_enabled: true,
  tip_suggestions: [10, 15, 20],
  supported_currencies: ['EUR', 'GBP', 'USD', 'XAF', 'XOF'],
};

// ---- External store (module-level) so non-React code (fmtMoney, the
// receipt printer, etc.) can read the active policy synchronously. ----
const _policyStore = {
  current: { ...DEFAULT_POLICY },
  listeners: new Set(),
  set(next) {
    this.current = { ...DEFAULT_POLICY, ...(next || {}) };
    this.listeners.forEach((cb) => { try { cb(); } catch (e) { /* noop */ } });
  },
  subscribe(cb) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  },
  get() {
    return this.current;
  },
};

export function getCurrentPolicy() {
  return _policyStore.current;
}

export function getCurrentCurrency() {
  return _policyStore.current.currency_code || 'USD';
}

/**
 * Push a freshly-saved policy doc into the store. Any React component
 * subscribed via `usePolicy()` will re-render immediately.
 */
export function notifyPolicyUpdated(next) {
  _policyStore.set(next);
}

const PolicyContext = createContext(null);

export function PolicyProvider({ children }) {
  const inFlight = useRef(false);

  const policy = useSyncExternalStore(
    (cb) => _policyStore.subscribe(cb),
    () => _policyStore.get(),
    () => _policyStore.get(),
  );

  const refresh = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await api.get('/policies/public');
      _policyStore.set(data);
    } catch (e) {
      // Keep defaults if the call fails — never block the UI.
    } finally {
      inFlight.current = false;
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    policy,
    refresh,
    fmt: (value) => formatMoney(value, policy.currency_code),
  }), [policy]);

  return <PolicyContext.Provider value={value}>{children}</PolicyContext.Provider>;
}

export function usePolicy() {
  const ctx = useContext(PolicyContext);
  if (ctx) return ctx;
  // Fallback (e.g. components rendered outside PolicyProvider in tests).
  return {
    policy: _policyStore.get(),
    refresh: () => {},
    fmt: (v) => formatMoney(v),
  };
}

// ---------------------------------------------------------------------------
// Currency formatting helpers
// ---------------------------------------------------------------------------
const CURRENCY_LOCALE = {
  USD: 'en-US',
  EUR: 'en-IE',
  GBP: 'en-GB',
  XOF: 'fr-FR',
  XAF: 'fr-FR',
};

const CURRENCY_LABEL = {
  USD: '$ USD — US Dollar',
  EUR: '€ EUR — Euro',
  GBP: '£ GBP — British Pound',
  XOF: 'CFA XOF — West African Franc',
  XAF: 'CFA XAF — Central African Franc',
};

export function currencyLabel(code) {
  return CURRENCY_LABEL[code] || code;
}

export function formatMoney(value, currency) {
  const cur = (currency || getCurrentCurrency() || 'USD').toUpperCase();
  const loc = CURRENCY_LOCALE[cur] || 'en-US';
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat(loc, { style: 'currency', currency: cur }).format(n);
  } catch (e) {
    if (cur === 'XOF' || cur === 'XAF') return `${Math.round(n).toLocaleString('fr-FR')} CFA`;
    return `${cur} ${n.toFixed(2)}`;
  }
}
